import * as React from 'react';
import { Table } from 'reactstrap';

export type DataGridSortOrder = 'asc' | 'desc'

export interface IColumn<T> {
  header: JSX.Element | string
  property?: keyof T
  render?: (record: T, subComponentShown: boolean) => JSX.Element
  align?: 'left' | 'right' | 'center' | 'justify'
  width?: number
  hidden?: boolean
  sortable?: boolean
}

interface IDataGridProps<T> {
  records: T[]
  columns: Array<IColumn<T>>
  disableStripe?: boolean
  sortBy?: keyof T
  sortOrder?: DataGridSortOrder
  className?: string
  small?: boolean
  hideInputs?: boolean
  initialPage?: number
  pageSize?: number
  onSortChange?: (sortBy: keyof T, sortOrder: DataGridSortOrder) => void
  subComponent?: (record: T) => JSX.Element;
  showSubComponent?: (record: T) => boolean;
  customSortMethod?: boolean;
}

interface IDataGridState<T> {
  pager: IPager
  pageSize: number
  totalRecords: number
  initialPage: number
}

interface IPager {
  currentPage: number
  pageSize: number
  totalPages: number
  startPage: number
  endPage: number
  startIndex: number
  endIndex: number
  pages: number[]
}

export class DataGrid<T> extends React.Component<IDataGridProps<T>, IDataGridState<T>> {
  state: IDataGridState<T> = {
    pager: {
      currentPage: 0,
      pageSize: 0,
      totalPages: 0,
      startPage: 0,
      endPage: 0,
      startIndex: 0,
      endIndex: 0,
      pages: [],
    },
    pageSize: 10,
    totalRecords: 0,
    // Preserves initial page as current page on componentDidUpdate
    initialPage: 1,
  }

  componentWillMount() {
    // set page size before attempting to set the page
    if (this.props.pageSize) {
      this.state.pageSize = this.props.pageSize
    }
    // set page if items array isn't empty
    if (this.props.records && this.props.records.length) {
      this.setPage(this.props.initialPage);
      this.setState({ totalRecords: this.props.records.length })
    }
  }

  componentDidUpdate(prevProps, prevState) {
    // reset page if items array has changed
    if (this.props.records !== prevProps.records) {
      if (this.state.initialPage < 2) {
        this.setPage(this.props.initialPage);
      } else {
        this.setPage(this.state.initialPage);
      }
      if (this.props.records.length) {
        this.setState({ totalRecords: this.props.records.length })
      }
    }
    /**
     * After mounting DataGrid component. If page size is changed via props,
     * update page size and reset the pager object
     */
    if (this.props.pageSize !== prevProps.pageSize) {
      this.setState({pageSize: this.props.pageSize}, () => {
        this.setPage(1);
      })
    }
  }

  getSortedRecords(): T[] {
    const { sortBy, sortOrder, customSortMethod } = this.props
    const { pager } = this.state

    const isDescending = sortOrder === 'desc'
    const records = [...this.props.records]

    if (!sortBy || !sortOrder) {
      return records.slice(pager.startIndex, pager.endIndex + 1)
    }

    if (!customSortMethod) {
      records.sort((r1, r2) => {
        const a1 = r1[sortBy];
        const a2 = r2[sortBy];

        if (a1 > a2) {
          return isDescending ? 1 : -1;
        } else if (a2 > a1) {
          return isDescending ? -1 : 1;
        }

        return 0;
      })
    }

    return records.slice(pager.startIndex, pager.endIndex + 1)
  }

  headerClicked(property: keyof T) {
    const { sortBy, sortOrder, onSortChange } = this.props
    let newSortOrder: DataGridSortOrder = 'desc'

    if (sortBy === property) {
      newSortOrder = sortOrder === 'asc' ? 'desc' : 'asc'
    }

    if (onSortChange) {
      onSortChange(property, newSortOrder)
    }
  }

  setPage(page) {
    const { records } = this.props;
    let pager: IPager = this.state.pager;
    const { pageSize } = this.state

    if (page < 1 || page > pager.totalPages) {
      return;
    }

    // get new pager object for specified page
    pager = this.getPager(records.length, page, pageSize);

    // get new page of items from items array
    //var pageOfItems = records.slice(pager.startIndex, pager.endIndex + 1)

    // update state
    this.setState({ pager, initialPage: pager.currentPage });
  }

  getPager(totalItems, requestedCurrentPage, pageSize) {
    // default to first page
    let currentPage = requestedCurrentPage || 1;

    // default page size is 10
    pageSize = Number(pageSize || 10);

    // calculate total pages
    const totalPages = Math.ceil(totalItems / pageSize);

    if (currentPage > totalPages) { currentPage = totalPages }

    let startPage
    let endPage
    if (totalPages <= 10) {
      // less than 10 total pages so show all
      startPage = 1;
      endPage = totalPages;
    } else {
      // more than 10 total pages so calculate start and end pages
      if (currentPage <= 6) {
        startPage = 1;
        endPage = 10;
      } else if (currentPage + 4 >= totalPages) {
        startPage = totalPages - 9;
        endPage = totalPages;
      } else {
        startPage = currentPage - 5;
        endPage = currentPage + 4;
      }
    }

    // calculate start and end item indexes
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize - 1, totalItems - 1);

    // create an array of pages to ng-repeat in the pager control
    const pages = [...Array((endPage + 1) - startPage).keys()].map((i) => startPage + i);

    // return object with all pager properties required by the view
    return {
      totalItems,
      currentPage,
      pageSize,
      totalPages,
      startPage,
      endPage,
      startIndex,
      endIndex,
      pages
    };
  }

  setPageSize(e) {
    const { pager, totalRecords } = this.state
    let { pageSize } = this.state
    e.preventDefault()

    pageSize = (e.currentTarget.value === 'all') ? totalRecords : e.currentTarget.value

    this.setState({ pageSize }, () => this.setPage(pager.currentPage))
  }

  render() {
    const { columns, sortBy, subComponent, className = '', showSubComponent = () => false, disableStripe = false, hideInputs } = this.props
    const { pager } = this.state
    const classNames: string[] = ['table-responsive', 'dataGrid']
    const visibleColumns = columns.filter((c) => c.hidden !== true)

    const records = this.getSortedRecords()

    if (className) {
      classNames.push(className)
    }

    if (this.props.small) {
      classNames.push('table-sm');
    }

    return (
      <section className={classNames.join(' ')}>
        {!hideInputs &&
          <section className="center-align" style={{ paddingTop: '5px' }}>
            <ul className="pagination" style={{ display: 'inline-block', paddingLeft: '0px', margin: '20px 0px', borderRadius: '4px' }}>
              <li style={{ padding: '5px' }}>Show Records</li>
              <li style={{
                marginRight: '10px', border: '1px solid rgb(221, 221, 221)', borderRadius: '4px'
              }}>
                <select className="browser-default" style={{ height: '32px' }} onChange={(e) => this.setPageSize(e)}>
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                  <option value="all">All</option>
                </select>
              </li>
              <li className={pager.currentPage === 1 ? 'disabled' : ''}>
                <a onClick={() => this.setPage(1)} style={{ cursor: 'pointer', marginLeft: '0px', borderTopLeftRadius: '4px', borderBottomLeftRadius: '4px', color: '#337ab7', border: '1px solid #ddd' }}>First</a>
              </li>
              <li className={pager.currentPage === 1 ? 'disabled' : ''}>
                <a onClick={() => this.setPage(pager.currentPage - 1)} style={{ cursor: 'pointer', color: '#337ab7', border: '1px solid #ddd' }}>Previous</a>
              </li>
              {pager.pages.map((page, index) =>
                <li key={index} className={pager.currentPage === page ? 'active' : ''}>
                  <a onClick={() => this.setPage(page)} style={{ cursor: 'pointer', color: '#337ab7', border: '1px solid #ddd' }}>{page}</a>
                </li>
              )}
              <li className={pager.currentPage === pager.totalPages ? 'disabled' : ''}>
                <a onClick={() => this.setPage(pager.currentPage + 1)} style={{ cursor: 'pointer', color: '#337ab7', border: '1px solid #ddd' }}>Next</a>
              </li>
              <li className={pager.currentPage === pager.totalPages ? 'disabled' : ''}>
                <a onClick={() => this.setPage(pager.totalPages)} style={{ cursor: 'pointer', color: '#337ab7', border: '1px solid #ddd' }}>Last</a>
              </li>
            </ul>
          </section>
        }
        <Table striped={!disableStripe} className="mb-0">
          <thead>
            <tr>
              {visibleColumns.map((column, index) => {
                const { property } = column;
                const { align = 'left', sortable = typeof sortBy === 'string' && typeof property === 'string' } = column
                const isSortedByProp = column.property === sortBy
                const thStyle: React.CSSProperties = {}
                const classes: string[] = [`text-${align}`, 'pl-3', 'bg-white']
                const thKey: string = `${column.header.toString()}-${index}`;

                if (sortable) {
                  classes.push('sortable')

                  if (isSortedByProp) {
                    classes.push(this.props.sortOrder)
                  }
                }

                if (column.width) {
                  thStyle.width = column.width;
                }

                return (
                  <th className={classes.join(' ')} key={thKey} style={thStyle}>
                    {sortable ?
                      <a href="#" onClick={(event) => {
                        event.preventDefault();
                        this.headerClicked(column.property)
                      }}>
                        {column.header}
                      </a>
                      :
                      <span>{column.header}</span>
                    }
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {records.map((record, index) => {
              const shouldShowSubComponent = showSubComponent(record)
              const hasSubComponentAndShow = shouldShowSubComponent && subComponent
              const trClasses: string[] = [];

              // if (hasSubComponentAndShow) {
              //   trClasses.push('border-left', 'border-right', 'border-dark');
              // }

              return (
                <React.Fragment key={`${record[sortBy]}-${index}`}>
                  <tr className={trClasses.join(' ')}>
                    {visibleColumns.map((column, colIndex) => {
                      const { align = 'left' } = column
                      const tdClasses = ['p-0']
                      const tdStyle: React.CSSProperties = {}
                      const containerClasses = [`text-${align}`, 'p-3']
                      let key = `${index}-${colIndex}`

                      if (column.property) {
                        key = `${record[column.property]}${key}`
                      }

                      if (column.width) {
                        tdStyle.width = column.width;
                      }

                      if (hasSubComponentAndShow) {
                        containerClasses.push('border-top', 'border-dark')

                        if (colIndex === 0) {
                          containerClasses.push('border-left', 'border-dark')
                          tdClasses.push('innerSideSection')
                        } else if (colIndex === columns.length - 1) {
                          containerClasses.push('border-right')
                          tdClasses.push('innerSideSection')
                        }
                      }

                      return (
                        <td key={key} className={tdClasses.join(' ')} style={tdStyle}>
                          <section className={containerClasses.join(' ')}>
                            {column.render && column.render(record, !!hasSubComponentAndShow)}
                            {!column.render && column.property && record[column.property]}
                          </section>
                        </td>
                      )
                    })}
                  </tr>
                  {hasSubComponentAndShow &&
                    <tr className="shadow">
                      <td colSpan={100} className="p-0 border-top-0">
                        <section className="border-left border-top-0 border-bottom border-right border-dark">
                          <div className="border-top">
                            {subComponent(record)}
                          </div>
                        </section>
                      </td>
                    </tr>
                  }
                </React.Fragment>
              )
            }
            )}
          </tbody>
        </Table>
      </section>
    )
  }
}
